'use client';

import SectionWrapper from '@/components/_rebrand/craft/SectionWrapper';
import ease from '@/constants/ease';
import { StyleOverride } from '@/types/craft-style-overrides';
import cx from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { FC, useState } from 'react';
import MobileDrawerWithTextAndImage from '../MobileDrawerWithTextAndImage';
import { DesktopAccordion } from './DesktopAccordion';
import { ImagePreloader } from './ImagePreloader';

export type TextMediaAccordionProps = {
  title: React.ReactNode;
  features: {
    title: string;
    description: string | React.ReactNode;
    list?: string[];
    tag?: string;
    image: React.ReactNode;
  }[];
  styleOverrides?: StyleOverride;
  backgroundColor?: string;
}

const TextMediaAccordion: FC<TextMediaAccordionProps> = ({ title, features = [], styleOverrides, backgroundColor }) => {
  const [activeFeatureIndex, setFeatureIndex] = useState(0);
  const activeFeature = features[activeFeatureIndex];

  return (
    <SectionWrapper styleOverrides={styleOverrides}>
      <div className={cx('px-container-mobile md:px-container-desktop', backgroundColor ? backgroundColor : 'bg-white', !styleOverrides ? 'py-6 md:py-20' : 'py-1', 'relative overflow-hidden')}>
        <div className="mx-auto max-w-grid">
          <ImagePreloader features={features} />
          <div className="grid grid-cols-1 md:grid-cols-12 gap-y-9 relative z-1">
            {/* IMAGE CONTENT LEFT */}
            <div className="hidden md:flex md:col-span-6 lg:col-span-6 flex-col gap-4">
              <div className="flex relative overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeFeatureIndex}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      duration: 200 / 1000,
                      ease: ease.appear,
                    }}
                  >
                    {activeFeature.image}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            {/* TEXT CONTENT RIGHT */}
            <div className="md:col-span-6 lg:col-span-5 lg:col-start-8 flex flex-col gap-6">
              <div className="flex flex-col gap-3 items-start">
                <h2 className="text-h2 text-blue-900 md:text-h1 font-500 tracking-tight md:pl-3">{title}</h2>
              </div>
              <DesktopAccordion
                features={features}
                activeFeatureIndex={activeFeatureIndex}
                onClickCallback={(index) => setFeatureIndex(index)} />
            </div>

            <div className="flex md:hidden flex-wrap">
              {features.map((feature, index) => (
                <MobileDrawerWithTextAndImage
                  key={`text-media-feature-${index}`}
                  title={feature.title}
                  description={feature.description}
                  image={feature.image}
                  isActive={index === activeFeatureIndex}
                  onClickCallback={() => setFeatureIndex(index)}
                  titleTextColor="text-gray-900"
                  textColor="text-gray-700"
                  iconColor="#0D1D39"
                  borderColor="border-gray-200"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
};

export default TextMediaAccordion;
